import React from 'react';
import { useTracking } from 'react-tracking';
import { TrackEventInput } from '@updater/ui-tracker';

// Hook imports
import { useProgressBarData } from 'flows/core/hooks/use-progress-bar-data';

// Component imports
import ChatComponent from '../components/chat/chat-component/ChatComponent';
import { useVBAUIState } from './useVBAUIState';
import { useVBAData } from './useVBAData';

// Type imports
import { InitiatePayload } from 'flows/core/types/cards';

// Types
export interface ChatState {
  showVoice: boolean;
  isSidebarOpen: boolean;
  chatReadyState: 'loading' | 'animating' | 'ready';
}

export interface ChatActions {
  setShowVoice: (show: boolean) => void;
  setIsSidebarOpen: (open: boolean) => void;
  handleProgressBarAnimationComplete: () => void;
  handleSendMessageWithTracking: (
    messageText: string,
    files?: File[]
  ) => Promise<void>;
  handleSelectTask: (taskName: string) => void;
}

export interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

export interface ChatData {
  // Progress data
  showProgressBar: boolean;
  priorityCards: InitiatePayload[];
  completedCards: InitiatePayload[];
  currentMoveDate: string | null | undefined;

  // Chat data
  conversation: unknown;
  validConversation: { messages: unknown[] } | undefined;
  messages: Message[];
  isLoading: boolean;
  isStartingNewConversation: boolean;
  propertyName: string | null | undefined;
  pendingUserMessage: unknown;
  animateLastAssistant: boolean;

  // Core functions
  handleSendMessage: (messageText: string, files?: File[]) => Promise<void>;
  startNewConversation: () => void;
  flushPendingAssistantMessage: () => void;
  handleTypewriterDone: () => void;
  chatRef: React.RefObject<unknown>;
  chatSessionKey: string;
}

export interface useVirtualAssistantChatReturn {
  state: ChatState;
  actions: ChatActions;
  data: ChatData;
  trackEvent: (event: TrackEventInput<unknown>) => void;
  ChatComponent: typeof ChatComponent;
  chatComponentProps: {
    uiState: ReturnType<typeof useVBAUIState>;
    data: ReturnType<typeof useVBAData>;
    handleSidebarClose: () => void;
    handleSelectTask: (task: string) => void;
    stableHandleSendMessageWithTracking: (msg: string, files?: File[]) => void;
    stableOnTypewriterDone: () => void;
    onBack?: () => void;
  };
}

/**
 * Custom hook that encapsulates all the chat logic from VirtualBuildingAssistant
 * Makes the chat reusable across different pages
 */
export function useVirtualAssistantChat(options?: {
  onBack?: () => void;
}): useVirtualAssistantChatReturn {
  const progressData = useProgressBarData();

  // Wait for progress data to stabilize before making any decisions
  const [progressDataStable, setProgressDataStable] = React.useState(false);
  const [lastProgressState, setLastProgressState] = React.useState<{
    showProgressBar: boolean;
    hasCards: boolean;
  } | null>(null);

  const uiState = useVBAUIState(
    progressData.showProgressBar,
    progressDataStable
  );

  const data = useVBAData(uiState.chatReadyState);

  const [initialLoadTime] = React.useState(() => Date.now());
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const stableOnTypewriterDone = React.useCallback(() => {
    data.handleTypewriterDone();
  }, [data.handleTypewriterDone]);

  const { trackEvent } = useTracking<TrackEventInput<unknown>>({
    domain: 'virtual-building-assistant',
    pageName: 'vba-chat',
  });

  // Enhanced message handler with tracking
  const handleSendMessageWithTracking = React.useCallback(
    async (messageText: string, files?: File[]) => {
      trackEvent({
        verb: 'sent',
        object: 'vba-message',
        details: {
          messageLength: messageText.length,
          hasFiles: files && files.length > 0,
          fileCount: files?.length || 0,
        },
      });

      await data.handleSendMessage(messageText, files);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trackEvent, data.handleSendMessage]
  );

  // Task selection handler
  const handleSelectTask = React.useCallback(
    (taskName: string) => {
      const prompt = `Can you help me complete the ${taskName} task?`;
      void handleSendMessageWithTracking(prompt);
    },
    [handleSendMessageWithTracking]
  );

  const handleSidebarClose = React.useCallback(() => {
    if (data.animateLastAssistant) {
      data.handleTypewriterDone();
    }
    uiState.setIsSidebarOpen(false);
  }, [
    data.animateLastAssistant,
    data.handleTypewriterDone,
    uiState.setIsSidebarOpen,
  ]);

  const stableHandleSendMessageWithTracking = React.useCallback(
    async (messageText: string, files?: File[]) => {
      console.log('🔧 Stable handleSendMessageWithTracking called');
      return handleSendMessageWithTracking(messageText, files);
    },
    [handleSendMessageWithTracking]
  );

  React.useEffect(() => {
    const currentState = {
      showProgressBar: progressData.showProgressBar,
      hasCards:
        progressData.priorityCards !== undefined &&
        progressData.completedCards !== undefined,
    };

    // Only proceed if we have complete card data
    if (!currentState.hasCards) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      console.log('⏹️ Cleared previous stabilization timeout');
    }

    if (!lastProgressState) {
      setLastProgressState(currentState);

      // Wait minimum 1200ms from initial load to allow backend to fully settle
      const minWaitTime = 1200;
      const elapsedTime = Date.now() - initialLoadTime;
      const remainingWait = Math.max(0, minWaitTime - elapsedTime);

      timeoutRef.current = setTimeout(() => {
        setProgressDataStable(true);
        timeoutRef.current = null;
      }, remainingWait + 200);
      return;
    }

    // Check if the data has changed
    const hasChanged =
      lastProgressState.showProgressBar !== currentState.showProgressBar ||
      lastProgressState.hasCards !== currentState.hasCards;

    if (hasChanged) {
      setLastProgressState(currentState);
      // Reset stability immediately when data changes
      setProgressDataStable(false);

      // Set new timeout for stabilization
      timeoutRef.current = setTimeout(() => {
        setProgressDataStable(true);
        timeoutRef.current = null;
      }, 200);
    }
  }, [
    progressData.showProgressBar,
    progressData.priorityCards,
    progressData.completedCards,
    lastProgressState,
    initialLoadTime,
  ]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const chatComponentProps = React.useMemo(
    () => ({
      uiState,
      data,
      handleSidebarClose,
      handleSelectTask,
      stableHandleSendMessageWithTracking,
      stableOnTypewriterDone,
      onBack: options?.onBack,
    }),
    [
      uiState,
      data,
      handleSidebarClose,
      handleSelectTask,
      stableHandleSendMessageWithTracking,
      stableOnTypewriterDone,
      options?.onBack,
    ]
  );

  return {
    state: {
      showVoice: uiState.showVoice,
      isSidebarOpen: uiState.isSidebarOpen,
      chatReadyState: uiState.chatReadyState,
    },
    actions: {
      setShowVoice: uiState.setShowVoice,
      setIsSidebarOpen: uiState.setIsSidebarOpen,
      handleProgressBarAnimationComplete:
        uiState.handleProgressBarAnimationComplete,
      handleSendMessageWithTracking,
      handleSelectTask,
    },
    data,
    trackEvent,
    ChatComponent,
    chatComponentProps,
  };
}
